<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { screens, playerTruth } from '../store';
  import type { MapEdge, MapNode } from './models/map.type';
  import { Network } from 'vis-network';

  import Portrait from './components/portrait.svelte';
  import Scrollable from './components/scrollable.svelte';
  import Icon from './icon/icon.svelte';

  import { mergeIcons } from './icon/icons';

  import { saveGame } from './utils/savefile';
  import { continueTutorial } from './utils/tutorial';
  import { Screen } from './models/screen.type';
  import { MapNodeType } from './models/map.type';
  import { TutorialEvent } from './utils/tutorial';

  let mapEl: HTMLDivElement;
  let networkMap: Network | undefined;
  // Raw: visiting a node writes to it, and those writes have to reach the node
  // the map draws, not a proxy of it.
  let selected = $state.raw<MapNode | undefined>(undefined);

  interface Props {
    data: any;
  }

  let { data }: Props = $props();
  let map = data.map;
  let experienceRequired = $state(($playerTruth.level + 1) * 5 * 3);

  screens.subscribe((value) => {
    // if last value equal map
    let last = value.findLast((value) => {
      return value.state == 'visible';
    });
    if (mapEl && last && last.type == Screen.Map) {
      // The player is changed in place while away -- in battle, in events -- so
      // hand the store the same object again to make everything showing it redraw.
      playerTruth.set($playerTruth);
      updateMap();
      saveGame(map, $playerTruth);

      continueTutorial({ type: TutorialEvent.Map, value: 'map' });
    }
    experienceRequired = ($playerTruth.level + 1) * 5 * 3;
  });

  function openNode(node: MapNode) {
    node.color = 'lightgreen';
    node.open = true;

    map.edges
      .filter((edge: MapEdge) => {
        return edge.from == node.id || edge.to == node.id;
      })
      .forEach((edge: MapEdge) => {
        edge.dashes = false;
        let nextNodeId = edge.to == node.id ? edge.from : edge.to;
        let nextNode = map.nodes.find((node: MapNode) => {
          return node.id == nextNodeId;
        });

        if (nextNode.open === false) {
          nextNode.color = 'lightgreen';
          nextNode.open = true;
        }
      });
  }

  function visitNode() {
    if (!selected) {
      return;
    }
    let node = selected;
    selected = undefined;

    var result = node.visit();
    if (!result) {
      openNode(node);
      node.color = 'white';
      node.visited = true;
      node.type = MapNodeType.Clear;
      node.image = '';
      node.shape = 'circle';
      updateMap();
      saveGame(map, $playerTruth);
    } else {
      result.then((outcome: string) => {
        if (outcome == 'win') {
          openNode(node);
          node.color = 'white';
          node.visited = true;
          node.type = MapNodeType.Clear;
          node.image = '';
          node.shape = 'circle';
          updateMap();
          saveGame(map, $playerTruth);
        }
      });
    }
  }

  function levelUp() {
    if ($playerTruth.experience >= experienceRequired) {
      $playerTruth.experience -= experienceRequired;
      screens.push(Screen.Upgrade);
    }
  }

  function updateMap() {
    map.nodes.forEach((node: MapNode) => {
      if (node.scouted && node.icon) {
        node.image = mergeIcons(node.icon, 'scouted');
      }
    });

    //set edges to point towards the cleared node
    map.edges.forEach((edge: MapEdge) => {
      let fromNode = map.nodes.find((node: MapNode) => {
        return node.id == edge.from;
      });
      let toNode = map.nodes.find((node: MapNode) => {
        return node.id == edge.to;
      });

      if (fromNode.visited) {
        edge.from = edge.to;
        edge.to = fromNode.id;
      } else if (!toNode.visited && fromNode.open) {
        edge.from = edge.to;
        edge.to = fromNode.id;
      }
    });
    networkMap?.setData(map);
  }

  const selectNode = (event: any) => {
    let node = map.nodes.find((node: MapNode) => {
      return node.id === event.nodes[0];
    });

    selected = node;

    continueTutorial({ type: TutorialEvent.NodeSelected, value: node.event?.type || node.type });
  };

  onMount(() => {
    let mapOptions = {
      interaction: {
        dragNodes: false,
        dragView: false,
        zoomView: false,
        multiselect: false,
        // selectConnectedEdges: false
      },
      physics: {
        // enabled: false
      },
      groups: {
        useDefaultGroups: true,
      },
      layout: {
        improvedLayout: true,
      },
    };

    // if(map == undefined){
    // map.set(data.map);
    openNode(map.nodes[0]);
    map.nodes[0].visited = true;
    map.nodes[0].color = 'white';
    // }
    if (!networkMap) {
      networkMap = new Network(mapEl, map, mapOptions);
      /* $networkMap.clustering.cluster({
        joinCondition: function (nodeOptions) {
          console.log(nodeOptions)
          return false;
        }
      }); */

      networkMap.on('selectNode', selectNode);
    } else {
      // add network map div to page
      // @ts-ignore
      mapEl.appendChild(networkMap.body.container);
    }
    updateMap();
  });

  /**
   * vis-network watches its own frame with a ResizeObserver and, on any size change,
   * reallocates the canvas backing buffer. Assigning canvas.width/height clears the
   * canvas, so while the scrollable was zooming -- which resizes the frame on every
   * frame of the gesture -- the graph was being wiped and redrawn continuously, and
   * read as disappearing.
   *
   * Holding the canvas at its current size for the gesture lets it stretch with the
   * background image instead (a little soft while zooming), then snap back to full
   * resolution once the zoom settles.
   */
  const pauseNetworkResize = () => {
    networkMap?.setOptions({ autoResize: false });
  };

  const resumeNetworkResize = () => {
    networkMap?.setOptions({ autoResize: true });
    networkMap?.redraw();
  };

  onDestroy(() => {
    networkMap?.off('selectNode', selectNode);
  });
</script>

<div class="container">
  <div class="player">
    <Portrait data={$playerTruth} />

    <div class="details">
      <div class="section">
        <div class="stat">
          <div class="stat-label">
            <Icon name="blood" />
            <h3>Health</h3>
            <span class="rule"></span>
          </div>
          <div class="bar-container red" style="--notches: {$playerTruth.healthMax}">
            <div
              class="bar-inner"
              style="width: {($playerTruth.healthCurrent / $playerTruth.healthMax) * 100}%"
            ></div>
            <div class="notches"></div>
            <p>{$playerTruth.healthCurrent} / {$playerTruth.healthMax}</p>
          </div>
        </div>

        <div class="stat">
          <div class="stat-label">
            <Icon name="level" />
            <h3>Experience</h3>
            <span class="rule"></span>
            <span class="stat-value">Lvl {$playerTruth.level}</span>
          </div>
          <div
            class="bar-container yellow"
            class:ready={$playerTruth.experience >= experienceRequired}
            role="button"
            tabindex="0"
            aria-disabled={$playerTruth.experience < experienceRequired}
            onclick={levelUp}
            onkeydown={() => {}}
          >
            <div
              class="bar-inner"
              style="width: {Math.min(100, ($playerTruth.experience / experienceRequired) * 100)}%"
            ></div>
            {#if $playerTruth.experience >= experienceRequired}
              <p>Level Up!</p>
            {:else}
              <p>{$playerTruth.experience} / {experienceRequired}</p>
            {/if}
          </div>
        </div>
      </div>

      <div class="section">
        <div class="stat">
          <div class="stat-label">
            <Icon name="coin" />
            <h3>Money</h3>
            <span class="rule"></span>
          </div>
          <div class="plaque">
            <p>{$playerTruth.money}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="map-container">
    <Scrollable zoomable={true} onZoomStart={pauseNetworkResize} onZoomEnd={resumeNetworkResize}>
      <div class="map" bind:this={mapEl}></div>
    </Scrollable>
  </div>

  {#if selected?.type == MapNodeType.Battle}
    <div class="encounter battle">
      {#if selected?.scouted}
        <div class="portrait battle">
          <Portrait data={selected?.battle?.enemy} />
        </div>

        <div class="details">
          <div class="stat-label">
            <h3 class="type">Battle</h3>
            <span class="rule"></span>
            <span class="stat-value">Lvl {selected?.battle?.level}</span>
          </div>
          <h2>{selected?.battle?.enemy?.title}</h2>
          <p>{selected?.battle?.enemy?.text}</p>

          {#if selected?.open}
            <button onclick={visitNode} class="btn"> Start Battle </button>
          {:else}
            <p class="locked">
              <Icon name="lock" />
              Cannot reach location
            </p>
          {/if}
        </div>
      {:else}
        <div class="portrait battle">
          <div class="frame">?</div>
        </div>

        <div class="details">
          <div class="stat-label">
            <h3 class="type">Battle</h3>
            <span class="rule"></span>
          </div>
          <h2>Unknown Enemy</h2>
          {#if selected?.open}
            <button onclick={visitNode} class="btn"> Start </button>
          {:else}
            <p class="locked">
              <Icon name="lock" />
              Cannot reach location
            </p>
          {/if}
        </div>
      {/if}
    </div>
  {:else if selected?.type == MapNodeType.Event && selected.event}
    <div class="encounter {selected.event.type}">
      {#if selected?.scouted || selected?.open}
        <div class="portrait {selected.event.type}">
          <Portrait data={{ type: selected.event.image }} />
        </div>

        <div class="details">
          <div class="stat-label">
            <h3 class="type">{selected.event.type}</h3>
            <span class="rule"></span>
          </div>
          <h2>{selected.event.title}</h2>
          <p>
            {selected.event?.text}
          </p>
          {#if selected?.open}
            <button onclick={visitNode} class="btn"> Start </button>
          {:else}
            <p class="locked">
              <Icon name="lock" />
              Cannot reach location
            </p>
          {/if}
        </div>
      {:else}
        <div class="portrait {selected.event.type}">
          <div class="frame">?</div>
        </div>

        <div class="details">
          <div class="stat-label">
            <h3 class="type">{selected.event.type}</h3>
            <span class="rule"></span>
          </div>
          <p>It could be anything</p>
          {#if selected?.open}
            <button onclick={visitNode} class="btn"> Start </button>
          {:else}
            <p class="locked">
              <Icon name="lock" />
              Cannot reach location
            </p>
          {/if}
        </div>
      {/if}
    </div>
  {:else if selected?.type == MapNodeType.Boss}
    <div class="encounter boss">
      {#if selected?.scouted || selected?.open}
        <div class="portrait boss">
          <Portrait data={selected?.battle?.enemy} />
        </div>

        <div class="details">
          <div class="stat-label">
            <h3 class="type">Boss</h3>
            <span class="rule"></span>
            <span class="stat-value">Lvl {selected?.battle?.level}</span>
          </div>
          <h2>{selected?.battle?.enemy?.title}</h2>
          <p>
            {selected.battle?.enemy.text}
          </p>

          {#if selected?.open}
            <button onclick={visitNode} class="btn"> Start Battle </button>
          {:else}
            <p class="locked">
              <Icon name="lock" />
              Cannot reach location
            </p>
          {/if}
        </div>
      {:else}
        <div class="portrait boss">
          <div class="frame">?</div>
        </div>

        <div class="details">
          <div class="stat-label">
            <h3 class="type">Boss</h3>
            <span class="rule"></span>
          </div>
          <p>It could be anything</p>
          {#if selected?.open}
            <button onclick={visitNode} class="btn"> Start </button>
          {:else}
            <p class="locked">
              <Icon name="lock" />
              Cannot reach location
            </p>
          {/if}
        </div>
      {/if}
    </div>
  {/if}
</div>

<style lang="less">
  @import '../defines.less';
  @import '../styles/bars.less';

  :root {
    background-color: var(--background-colour);
  }

  .container {
    display: grid;
    width: 100%;
    height: 100%;
    grid-template-columns: var(--column-size) 1fr var(--column-size);

    @media @tall {
      grid-template-columns: 1fr;
      grid-template-rows: var(--column-size) minmax(0, 1fr) var(--column-size);
    }

    // Sized by its grid track, not the viewport: on a phone 100vh is the height
    // with the browser's bars hidden, taller than what is actually on screen,
    // and in portrait that pushed the encounter panel -- and its Start button --
    // off the bottom.
    .map-container {
      width: 100%;
      height: 100%;
      min-height: 0;
      overflow: auto;

      .map {
        width: 100%;
        height: 100%;
        // width: 100%;
        // height: 100%;

        background-image: url('../assets/backgrounds/worldMap3.webp');
        background-size: cover;
        background-position: center;
      }
    }

    /* .encounter{
      &.enemy{
      }
      &.scout{
      }
    } */

    .portrait {
      height: var(--column-size);
      width: var(--column-size);
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: auto;
      box-shadow: inset black 0px 0px var(--icon-size) 10px;
      font-size: 250px;
      position: relative;
      background: var(--accent, transparent);
      color: var(--accent-dark, transparent);

      @media @large {
        font-size: 175px;
      }
      @media @medium {
        font-size: 140px;
      }
      @media @small {
        font-size: 120px;
      }

      .frame {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        border-style: solid;
        border-width: 4px;
        border-color: black;

        outline-style: solid;
        outline-width: 2px;
        outline-color: var(--accent, var(--background-colour));

        margin: 3px;
        box-shadow: 0 0 4px 4px var(--accent, var(--background-colour)) inset;

        @media @medium {
          border-width: 2px;
          margin: 2px;
        }
        @media @small {
          border-width: 1px;
          outline-width: 1px;
          box-shadow: 0 0 2px 2px var(--accent, var(--background-colour)) inset;
        }
      }
    }

    .details {
      display: flex;
      flex-direction: column;
      grid-gap: 15px;
      padding: 15px;
      text-align: left;

      h2 {
        margin: 0;
        font-size: 1.1rem;
        text-transform: capitalize;
      }
      p {
        margin: 0;
      }

      // Only soaks up slack, so a short panel keeps the button under the text
      // and a tall one parks it at the bottom.
      .btn {
        margin-top: auto;
      }
    }

    // Both side panels share one body: lit from the top, sunken at the edges,
    // with the battle screens' cloud texture over it so neither reads flat.
    .player,
    .encounter {
      position: relative;
      z-index: 1;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      background-image:
        radial-gradient(120% 60% at 50% 0%, rgba(255, 220, 170, 0.1), transparent 65%),
        linear-gradient(
          to bottom,
          var(--background-colour-light, #65290e),
          var(--background-colour-dark, #220e05)
        );
      box-shadow:
        inset 0 0 48px 12px rgba(0, 0, 0, 0.65),
        0 0 20px 4px rgba(0, 0, 0, 0.8);

      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        pointer-events: none;
        opacity: 0.35;
        background-image: url('../assets/effects/health-bar.jpg');
        mix-blend-mode: overlay;
        filter: blur(4px);
      }

      // Lift the panel contents above the cloud.
      :global(.portrait),
      .details {
        position: relative;
        z-index: 1;
      }

      :global(.portrait) {
        flex: none;
      }

      .stat-label {
        .stat-label-rules();
      }
    }

    .player {
      .details {
        grid-gap: 20px;
      }

      .section {
        display: flex;
        flex-direction: column;
        grid-gap: 12px;
      }

      .stat-label {
        margin-bottom: 5px;
      }
    }

    // The node's own colour, set once here and read by both the portrait and
    // the panel below it.
    .encounter {
      &.battle {
        --accent: #c72e17;
        --accent-dark: #50120a;
      }
      &.scout {
        --accent: #4caf50;
        --accent-dark: #388e3c;
      }
      &.campfire {
        --accent: #ff9800;
        --accent-dark: #e65100;
      }
      &.event {
        --accent: #ffeb3b;
        --accent-dark: #8d6e00;
      }
      &.quest {
        --accent: #2196f3;
        --accent-dark: #0d47a1;
      }
      &.vendor {
        --accent: #26a69a;
        --accent-dark: #00695c;
      }
      &.boss {
        --accent: #9370db;
        --accent-dark: #4b0082;
      }

      .details {
        flex: 1;

        .type {
          color: var(--accent, rgba(255, 255, 255, 0.75));
        }

        .rule {
          opacity: 0.55;
          background-image: linear-gradient(
            to right,
            var(--accent, rgba(255, 255, 255, 0.3)),
            transparent
          );
        }

        // A blocked action, not another line of body copy.
        .locked {
          --icon-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          grid-gap: 8px;
          margin-top: auto;
          font-size: 0.8rem;
          font-style: italic;
          color: rgba(255, 255, 255, 0.5);
        }
      }
    }

    .bar-container,
    .plaque {
      .stat-frame();
    }

    .bar-container {
      .stat-bar();

      // One tick per point of health, matching the notched battle bar.
      .notches {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1;
        background-image: repeating-linear-gradient(
          to right,
          transparent 0,
          transparent calc(100% / var(--notches, 10) - 1px),
          rgba(255, 255, 255, 0.25) calc(100% / var(--notches, 10) - 1px),
          rgba(255, 255, 255, 0.25) calc(100% / var(--notches, 10))
        );
      }

      &.red {
        .stat-bar-red();
      }
      &.yellow {
        .stat-bar-yellow();
      }
    }

    .plaque {
      .stat-plaque();
    }

    .stat-keyframes();
  }

  @media @tall {
    .container {
      grid-template-columns: 1fr;
      grid-template-rows: var(--column-size) minmax(0, 1fr) var(--column-size);
      // viewport-fit=cover lets an edge-to-edge Android draw the page under its
      // system buttons; keep the encounter panel clear of them.
      padding-bottom: env(safe-area-inset-bottom);
      box-sizing: border-box;

      .player {
        flex-direction: row;

        .details {
          flex-direction: row;
          // `safe`: plain centring spills overflow past the top as well as the
          // bottom, and a scroller cannot reach anything above its top edge.
          align-content: safe center;
          flex-wrap: wrap;
          overflow: auto;
          width: calc(100vw - var(--column-size) - 20px);
          padding: 10px;

          .section {
            min-width: 150px;
            flex: 1;
            justify-content: center;
          }
        }
      }

      .encounter {
        flex-direction: row;

        // The panel is only a portrait tall here, so the description scrolls
        // on its own and the button stays on screen below it.
        .details {
          width: calc(100vw - var(--column-size) - 20px);
          overflow: hidden;
          grid-gap: 8px;
          padding: 10px;

          > * {
            flex: none;
          }

          > p:not(.locked) {
            flex: 0 1 auto;
            min-height: 0;
            overflow: auto;
          }
        }
      }
    }
  }
  /* .asd{
    // yellow
    color: #FFEB3B;
    color: #8D6E00;

    color: #2196F3;
    color: #0D47A1;

    color: #FF9800;
    color: #E65100;

    color: #4CAF50;
    color: #388E3C;
  } */
</style>
